import httpService from '@src/utility/httpService';
import { TOP_FILTER_URL, Top_Filter_singleTin } from '@src/CommonComponents/constant/api_url_constants';
import { checkIsInternal, checkMultiTin, checkSingleTin } from '@src/utility/createStorage';
import { config } from '@uhg-abyss/web/tools/config';

const _httpService = new httpService();
if (checkIsInternal()) {
  _httpService.setBaseURL(config('API_DOMAIN_INTERNAL') || '');
} else {
  _httpService.setBaseURL(config('API_DOMAIN_EXTERNAL') || '');
}

// Build payload for VBC filter with proper endpoint selection
// Follows the three-tier pattern: Extended-TINs (Multi/Single) or Standard-Search (HCO)
export const buildVbcTaxIdsPayload = ({ timePeriod, lob, providerSysKey, tins }: { timePeriod: string; lob: string; providerSysKey?: string; tins?: string[] }) => {
  const isMultiTin = checkMultiTin();
  const isSingleTin = typeof checkSingleTin === 'function' && checkSingleTin();
  
  // Multi-TIN Search: Use extended-tins endpoint for MULTITIN
  if (isMultiTin || (tins && tins.length > 1)) {
    const tinsList = tins && tins.length > 0 ? tins : [];
    const payload = {
      RequestedJourney: 'provTinListFilter',
      ProvTinListFilter: {
        Tin: tinsList.join(',')
      }
    };
    return {
      payload,
      endpoint: '/extended-tins/top-filter?items=MULTITIN'
    };
  }
  
  // Single-TIN Search: Use extended-tins endpoint for TIN
  if (isSingleTin || (tins && tins.length === 1)) {
    const tinToUse = tins && tins.length > 0 ? tins[0] : sessionStorage.getItem('multiSelectedTins')?.split(',')[0];
    if (tinToUse) {
      const payload = {
        RequestedJourney: 'provTinListFilter',
        ProvTinListFilter: {
          Tin: tinToUse
        }
      };
      return {
        payload,
        endpoint: '/extended-tins/top-filter?items=TIN'
      };
    }
  }
  
  // HCO/Provider Search: Use standard-search endpoint
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const base: any = {
    RequestedJourney: 'vbcFilter',
    VBCFilter: {
      TimeFilter: 'CalendarYear',
      TimeFilterText: timePeriod,
    }
  };
  
  if (lob) {
    base.VBCFilter.Lob = lob.toUpperCase();
  }
  
  if (providerSysKey) {
    base.VBCFilter.ProviderSysKey = providerSysKey;
    } else {
    const providerInfo = JSON.parse(sessionStorage.getItem('providerInfo') || '{}');
    if (providerInfo?.Tin) {
      base.VBCFilter.Tins = providerInfo.Tin;
    }
  }
  
  return {
    payload: base,
    endpoint: TOP_FILTER_URL
  };
};

// Build payload for extended tin single selection apply (provTinListFilter)
export const buildExtendedTinPayload = (tin: string) => ({
  RequestedJourney: 'provTinListFilter',
  ProvTinListFilter: { Tin: tin }
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const fetchVbcTaxIds = async (payloadWithEndpoint: any) => {
  try {
    // Extract payload and endpoint from the new structure
    let payload, endpoint;
    
    if (payloadWithEndpoint.payload && payloadWithEndpoint.endpoint) {
      payload = payloadWithEndpoint.payload;
      endpoint = payloadWithEndpoint.endpoint;
    } else {
      // Fallback for backward compatibility (if old format is passed)
      payload = payloadWithEndpoint;
      endpoint = TOP_FILTER_URL;
    }
    
    
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const resp: any = await _httpService.push(endpoint, payload);
    return resp?.Data ?? [];
  } catch (e) {
    console.error('fetchVbcTaxIds error - Payload was:', payloadWithEndpoint);
    console.error('fetchVbcTaxIds error - Error:', e);
    throw e;
  }
};

// Extended endpoint call for a specific TIN selection
export const fetchExtendedTinApply = async (tin: string) => {
  try {
    const payload = buildExtendedTinPayload(tin);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const resp: any = await _httpService.push(Top_Filter_singleTin, payload);
    return resp?.Data ?? [];
  } catch (e) {
    console.error('fetchExtendedTinApply error', e);
    return [];
  }
};

// Helper to format extended TIN data for header display
const getParStatus = (lob: string | undefined) => {
  if (!lob) {
    return 'N/A';
  } else {
    return lob.toLowerCase() === 'varies by mpin'
      ? lob
      : lob?.trim().split(' ').join(', ');
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const setHeaderValues = (data: any) => {
  const headerDetails = [
    data?.RegionName && {
      header: 'Region:',
      value: data?.RegionName?.toUpperCase(),
    },
    data?.State && { header: 'State:', value: data?.State?.toUpperCase() },
    data?.MarketName && {
      header: 'Market:',
      value: data?.MarketName?.toUpperCase(),
    },
    data?.MajorMarketName && !data?.MarketName && {
      header: 'Market:',
      value: data?.MajorMarketName?.toUpperCase(),
    },
    data?.MarketNumber && {
      header: 'Market Number:',
      value: data?.MarketNumber,
    },
    data?.TaxIdType && {
      header: 'Tax ID Type:',
      value: data?.TaxIdType?.toUpperCase(),
    },
    data?.SpecialtiesPrimary && {
      header: 'Specialty:',
      value: data?.SpecialtiesPrimary?.toUpperCase(),
    },
    data?.FinancialOwner && {
      header: 'Financial Ownership:',
      value: data?.FinancialOwner?.toUpperCase(),
    },
    data?.ParForLOB && {
      header: 'Par Status:',
      value: getParStatus(data?.ParForLOB)?.toUpperCase(),
    },
  ].filter(Boolean);
  return headerDetails;
};

// Fetch extended TIN details with full data
export const getVbcExtendedTinDetails = async (tin: string) => {
  try {
    const payload = buildExtendedTinPayload(tin);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const resp: any = await _httpService.push(Top_Filter_singleTin, payload);
    return resp?.Data?.[0] || null;
  } catch (e) {
    console.error('getVbcExtendedTinDetails error', e);
    return null;
  }
};
